import { useCallback, useContext, useEffect, useState } from 'react';
import { ApiContext } from '../components/ApiProvider';
import { getAllParticipants, getParticipantStepCount, isLeadBooker } from '../utils/booking';
import { areAllParticipantsCheckedIn, getParticipantStepStatus } from '../utils/participantStatus';

export const useParticipantStatuses = (currentFormData = null, currentParticipantIndex = null) => {
    const {
        booking,
        preCheckSettings,
        savedInSession,
        markParticipantAsSaved
    } = useContext(ApiContext);
    const [statuses, setStatuses] = useState({});

    // Get fields for a specific participant type
    const getFieldsForParticipant = useCallback((participantIndex) => {
        return isLeadBooker(participantIndex)
            ? preCheckSettings?.lead_booker_fields || []
            : preCheckSettings?.party_member_fields || [];
    }, [preCheckSettings]);

    // Calculate statuses for all participants
    const calculateStatuses = useCallback(() => {
        if (!booking || !preCheckSettings) return {};

        const newStatuses = {};
        const allParticipants = getAllParticipants(booking);
        const stepCount = getParticipantStepCount(booking, preCheckSettings);

        allParticipants.forEach((participant, index) => {
            const fields = getFieldsForParticipant(index);

            // Use current form data if this is the participant being edited
            const formData = (index === currentParticipantIndex) ? currentFormData : null;

            // Check if this participant was saved in current session
            const wasSavedInSession = savedInSession.has(index);

            // Pass session info to status calculation
            if (index < stepCount) {
                newStatuses[index] = getParticipantStepStatus(
                    participant,
                    fields,
                    formData,
                    booking,
                    preCheckSettings,
                    wasSavedInSession
                );
            }
        });

        return newStatuses;
    }, [booking, preCheckSettings, currentFormData, currentParticipantIndex, getFieldsForParticipant, savedInSession]);

    // Recalculate statuses when dependencies change
    useEffect(() => {
        const newStatuses = calculateStatuses();
        setStatuses(newStatuses);
    }, [calculateStatuses]);

    // Force recalculation (useful after save operations)
    const refreshStatuses = useCallback(() => {
        const newStatuses = calculateStatuses();
        setStatuses(newStatuses);
    }, [calculateStatuses]);

    // Get status for specific participant
    const getParticipantStatus = useCallback((index) => {
        return statuses[index] || 'incomplete';
    }, [statuses]);

    // Check if there are any unsaved steps
    const hasUnsavedSteps = useCallback(() => {
        return Object.values(statuses).includes('unsaved');
    }, [statuses]);

    // Check if all participants are ready for pre-checkin (including skipped)
    const areAllReady = useCallback(() => {
        if (!booking || !preCheckSettings) return false;

        const allParticipants = getAllParticipants(booking);
        const n = getParticipantStepCount(booking, preCheckSettings);

        return allParticipants.slice(0, n).every((participant, index) => {
            const fields = getFieldsForParticipant(index);
            const wasSavedInSession = savedInSession.has(index);
            const status = getParticipantStepStatus(participant, fields, null, booking, preCheckSettings, wasSavedInSession);
            // Lead Booker cannot be skipped — must be completed or saved
            if (isLeadBooker(index)) {
                return status === 'completed' || status === 'saved';
            }
            return status === 'completed' || status === 'saved' || status === 'skipped';
        });
    }, [booking, preCheckSettings, getFieldsForParticipant, savedInSession]);

    // Check if all participants are already pre-checked-in
    const areAllCheckedIn = useCallback(() => {
        return areAllParticipantsCheckedIn(booking, preCheckSettings);
    }, [booking, preCheckSettings]);

    return {
        statuses,
        getParticipantStatus,
        hasUnsavedSteps,
        areAllReady,
        areAllCheckedIn,
        refreshStatuses,
        markParticipantAsSaved
    };
}; 